import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery } from '@tanstack/react-query'

import { hashString } from '@/shared/utils/hashString'

// ============================================================================
// Shared Enums and Constants
// ============================================================================

export type ProjectionYear =
  | '2026'
  | '2027'
  | '2028'
  | '2029'
  | '2030'
  | '2031'
  | '2032'
  | '2033'
  | '2034'

export type ProjectionYearBand = '2026-2027' | '2028+'

export type CalculationType = 'default' | 'actual'

// ============================================================================
// Projection Types
// ============================================================================

export interface BaseProjection {
  volume: number
  netEmissions: number
  netEmissionsPerTonne: number
  totalEmissions: number
  directEmissions: number
  indirectEmissions: number
  freeAllocationTCo2: number
  specificFreeAllocationTCo2Pt: number
  cbamFactor: number
  benchmarkValue: number | null
  benchmarkYearBand: ProjectionYearBand
}

export interface LiabilityProjection extends BaseProjection {
  specificTotalEmbeddedEmissions?: number | null
  specificDirectEmbeddedEmissions?: number | null
  specificDirectEmbeddedEmissionsSource?: string
  specificIndirectEmbeddedEmissions?: number | null
}

export interface AggregatedProjection extends BaseProjection {
  specificTotalEmbeddedEmissions?: number | null
  specificDirectEmbeddedEmissions?: number | null
  specificDirectEmbeddedEmissionsSource?: string
  specificIndirectEmbeddedEmissions?: number | null
}

export type ProjectionByYear = Record<string, LiabilityProjection>
export type AggregatedProjectionByYear = Record<string, AggregatedProjection>

// ============================================================================
// Specific Embedded Emissions Types
// ============================================================================

export type SpecificEmbeddedEmissionsSource =
  | 'defaultValueTotalEmissions'
  | 'defaultValue2026IncludingMarkUp'
  | 'defaultValue2027IncludingMarkUp'
  | 'defaultValue2028OnwardsIncludingMarkUp'
  | 'actual'

export interface SpecificEmbeddedEmissions {
  /**
   * The specific direct embedded emissions per tonne used in the calculation
   * This is the same value across all years for real values,
   * but may vary by year for default values (due to mark-up values)
   */
  valuePerYear: Record<ProjectionYear, number | null>

  /**
   * The source of the emissions value
   */
  source: SpecificEmbeddedEmissionsSource

  /**
   * Additional details based on the source type
   */
  details?: {
    /**
     * For 'actual' source: the provided emissions values
     */
    provided?: {
      directEmissionsPerTonne: number
      indirectEmissionsPerTonne: number
      totalEmissionsPerTonne: number
    }
    /**
     * For default sources: available default values
     */
    available?: {
      defaultValueTotalEmissions: number | null
      defaultValueDirectEmissions: number | null
      defaultValueIndirectEmissions: number | null
      defaultValue2026IncludingMarkUp: number | null
      defaultValue2027IncludingMarkUp: number | null
      defaultValue2028OnwardsIncludingMarkUp: number | null
    }
  }
}

// ============================================================================
// Response Types
// ============================================================================

export interface LiabilityParameters {
  benchmark: {
    selected: {
      cnCode: string | null
      found: boolean
    }
    selectionReason: {
      lookupMethod: string
      cnCodeUsed: string
      marker: string | null
      note: string
    }
    usage: Record<
      string,
      {
        value: number | null
        yearBand: ProjectionYearBand
      }
    >
    validation: {
      benchmarkRouteMismatch: boolean
      noBenchmarkOptions: boolean
      noUsableBenchmarkValues: boolean
      reasonCodes: string[]
      availableBenchmarkRoutes: string[]
      defaultProductionRoute: string | null
    }
    disclaimer: {
      case: number
      message: string
    } | null
  }
  /**
   * The specific embedded emissions used in the calculation
   * Unified interface for both default and actual values
   */
  specificEmbeddedEmissions: SpecificEmbeddedEmissions
  /**
   * Additional metadata about how values were selected
   */
  selectionReason: {
    countryRequested: string
    countryUsed: string
    usedOtcFallback: boolean
    cnCodeVariantsChecked: string[]
    matchedCnCode: string
    [key: string]: unknown
  }
}

export interface BatchLiabilityResultItem {
  id?: string
  code: string
  calculationType: CalculationType
  result: {
    projection: ProjectionByYear
    parameters: LiabilityParameters
  } | null
  error: string | null
}

export interface BatchLiabilityResponse {
  results: BatchLiabilityResultItem[]
  aggregatedProjectionByQuarter: Record<string, Record<string, AggregatedProjection>>
  aggregatedProjection: AggregatedProjectionByYear
}

type BatchLiabilityPayload = {
  requests: {
    id?: string
    code: string
    country: string
    volume: number
    isActualValues: boolean
    directEmissionsPerTonne: number | null
    indirectEmissionsPerTonne: number | null
    cscfPercent?: number
    importDate?: string
    quarter?: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  }[]
}

const calculateBatchLiability = async (payload: BatchLiabilityPayload) => {
  const response = await apiClient.post<BatchLiabilityResponse>('/v1/liability/batch', payload, {
    timeout: 300000 // 5 minutes — batch operations can exceed default client timeout
  })
  return response.data
}

export const useCalculateBatchLiability = (payload: BatchLiabilityPayload, query?: string) => {
  const payloadHash =
    payload.requests.length > 0 ? hashString(JSON.stringify(payload.requests)) : ''
  return useQuery<BatchLiabilityResponse, Error>({
    queryKey: ['calculateBatchLiability', payloadHash, query],
    queryFn: () => calculateBatchLiability(payload),
    enabled: payload.requests.length > 0
  })
}

type CalculateDefaultLiabilityResponse = {
  projection: Record<string, LiabilityProjection>
  parameters: LiabilityParameters
}
type CalculateDefaultLiabilityPayload = {
  code: string
  country: string
  volume: string
  cscfPercent?: number
}

const calculateDefaultLiability = async (
  payload: CalculateDefaultLiabilityPayload
): Promise<CalculateDefaultLiabilityResponse> => {
  return apiClient
    .post<CalculateDefaultLiabilityResponse>('/v1/liability/default-values', payload)
    .then(response => response.data)
}

export const useCalculateDefaultLiability = (payload: CalculateDefaultLiabilityPayload) => {
  return useQuery({
    queryKey: ['calculateDefaultLiability', payload],
    queryFn: () => calculateDefaultLiability(payload),
    enabled: !!payload.code && !!payload.country && !!payload.volume && Number(payload.volume) > 0
  })
}

const downloadLiabilityOverviewPdfBlob = async (
  url: string,
  filename: string,
  lang?: string
): Promise<void> => {
  const response = await apiClient.get<Blob>(url, {
    responseType: 'blob',
    timeout: 30000,
    params: lang ? { lang } : undefined
  })

  const objectUrl = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

export const useDownloadImportedGoodLiabilityOverviewPdf = (importedGoodId: string) => {
  return useMutation({
    mutationFn: async (args: { filename: string; lang?: string }) => {
      await downloadLiabilityOverviewPdfBlob(
        `/v1/liability/overview-pdf/imported/${importedGoodId}`,
        args.filename,
        args.lang
      )
    }
  })
}

export const useDownloadSimulationGoodLiabilityOverviewPdf = (cbamSimulationGoodId: string) => {
  return useMutation({
    mutationFn: async (args: { filename: string; lang?: string }) => {
      await downloadLiabilityOverviewPdfBlob(
        `/v1/liability/overview-pdf/simulation/${cbamSimulationGoodId}`,
        args.filename,
        args.lang
      )
    }
  })
}
